import React, { useState } from 'react';

const plans = [
  {
    name: 'Starter',
    monthly: 0,
    annual: 0,
    description: 'For individuals exploring AI-driven decision making.',
    cta: 'Get Started Free',
    ctaStyle: 'border border-indigo-500 text-indigo-600 hover:bg-indigo-50',
    highlight: false,
    features: [
      '5 decision modules',
      'Up to 3 data sources',
      'Basic AI recommendations',
      'PDF export',
      'Community support',
    ],
  },
  {
    name: 'Pro',
    monthly: 29,
    annual: 19,
    description: 'For professionals who need full decision intelligence.',
    cta: 'Start Free Trial',
    ctaStyle: 'bg-indigo-600 text-white hover:bg-indigo-700',
    highlight: true,
    features: [
      'Unlimited decision modules',
      'Unlimited data sources',
      'Advanced AI recommendations',
      'Scenario planning & simulations',
      'Clarity Score on every decision',
      'PDF, Word & PowerPoint export',
      'Priority email support',
    ],
  },
  {
    name: 'Enterprise',
    monthly: 99,
    annual: 79,
    description: 'For teams that run on data-driven decisions at scale.',
    cta: 'Talk to Sales',
    ctaStyle: 'border border-gray-300 text-gray-700 hover:bg-gray-50',
    highlight: false,
    features: [
      'Everything in Pro',
      'Team collaboration & roles',
      'SSO & SAML authentication',
      'Custom integrations & API access',
      'Audit logs & compliance reports',
      'Dedicated success manager',
      'SLA guarantee',
    ],
  },
];

const faqItems = [
  {
    q: 'Can I change plans later?',
    a: 'Yes. You can upgrade or downgrade your plan at any time. Changes take effect at the next billing cycle.',
  },
  {
    q: 'Is there a free trial for Pro?',
    a: 'Absolutely — Pro comes with a 14-day free trial, no credit card required.',
  },
  {
    q: 'How does annual billing work?',
    a: 'Annual plans are billed once per year and offer up to 35% savings compared to monthly billing.',
  },
  {
    q: 'What payment methods do you accept?',
    a: 'We accept all major credit cards (Visa, Mastercard, Amex) and ACH bank transfers for Enterprise plans.',
  },
];

export default function Pricing() {
  const [annual, setAnnual] = useState(false);
  const [openFaq, setOpenFaq] = useState<number | null>(null);

  return (
    <div className="bg-[#f8fafc] pt-24 pb-20">
      {/* Header */}
      <div className="max-w-screen-xl mx-auto px-6 text-center mb-14">
        <h1
          className="text-4xl md:text-5xl font-bold text-gray-900 mb-4"
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          Simple, transparent pricing
        </h1>
        <p className="text-lg text-gray-500 max-w-xl mx-auto mb-8">
          Start free. Scale as you grow. No hidden fees, ever.
        </p>

        {/* Toggle */}
        <div className="inline-flex items-center gap-3 bg-white border border-gray-200 rounded-full px-2 py-1.5 shadow-sm">
          <button
            onClick={() => setAnnual(false)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              !annual ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Monthly
          </button>
          <button
            onClick={() => setAnnual(true)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors flex items-center gap-2 ${
              annual ? 'bg-indigo-600 text-white' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            Annual
            <span
              className="text-xs font-semibold px-2 py-0.5 rounded-full"
              style={{ background: annual ? 'rgba(255,255,255,0.25)' : 'rgba(99,102,241,0.12)', color: annual ? 'white' : '#4f46e5' }}
            >
              Save 35%
            </span>
          </button>
        </div>
      </div>

      {/* Plans */}
      <div className="max-w-screen-xl mx-auto px-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 items-start">
          {plans.map(({ name, monthly, annual: ann, description, cta, ctaStyle, highlight, features }) => (
            <div
              key={name}
              className={`rounded-2xl p-8 flex flex-col gap-6 ${
                highlight
                  ? 'bg-indigo-600 text-white shadow-xl shadow-indigo-200 relative'
                  : 'bg-white border border-gray-200 shadow-sm'
              }`}
            >
              {highlight && (
                <span className="absolute -top-3 left-1/2 -translate-x-1/2 text-xs font-bold bg-amber-400 text-amber-900 px-4 py-1 rounded-full">
                  Most Popular
                </span>
              )}
              <div>
                <h2
                  className={`text-lg font-bold mb-1 ${highlight ? 'text-white' : 'text-gray-900'}`}
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  {name}
                </h2>
                <p className={`text-sm ${highlight ? 'text-indigo-200' : 'text-gray-500'}`}>{description}</p>
              </div>

              <div className="flex items-end gap-1">
                <span
                  className={`text-5xl font-bold ${highlight ? 'text-white' : 'text-gray-900'}`}
                  style={{ fontFamily: 'Poppins, sans-serif' }}
                >
                  ${annual ? ann : monthly}
                </span>
                {(monthly > 0) && (
                  <span className={`text-sm mb-2 ${highlight ? 'text-indigo-200' : 'text-gray-400'}`}>
                    / mo{annual ? ', billed annually' : ''}
                  </span>
                )}
              </div>

              <button
                className={`w-full py-3 rounded-xl text-sm font-semibold transition-colors ${ctaStyle}`}
              >
                {cta}
              </button>

              <ul className="flex flex-col gap-3">
                {features.map(f => (
                  <li key={f} className="flex items-start gap-3 text-sm">
                    <svg
                      className={`w-5 h-5 flex-shrink-0 mt-0.5 ${highlight ? 'text-indigo-200' : 'text-indigo-500'}`}
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className={highlight ? 'text-indigo-100' : 'text-gray-600'}>{f}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>

      {/* FAQ */}
      <div className="max-w-2xl mx-auto px-6 mt-20">
        <h2
          className="text-2xl font-bold text-gray-900 text-center mb-8"
          style={{ fontFamily: 'Poppins, sans-serif' }}
        >
          Pricing FAQ
        </h2>
        <div className="flex flex-col gap-3">
          {faqItems.map(({ q, a }, i) => (
            <div key={i} className="bg-white border border-gray-200 rounded-xl overflow-hidden">
              <button
                className="w-full flex items-center justify-between px-6 py-4 text-left text-gray-900 font-medium hover:bg-gray-50 transition-colors"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
              >
                <span>{q}</span>
                <svg
                  className={`w-5 h-5 text-gray-400 flex-shrink-0 transition-transform duration-200 ${openFaq === i ? 'rotate-180' : ''}`}
                  fill="none" stroke="currentColor" viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              {openFaq === i && (
                <div className="px-6 pb-4 pt-2 text-gray-600 text-sm leading-relaxed border-t border-gray-100">
                  {a}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
